import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, ShieldCheck, Package, Globe2 } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/AppContext";
import SEO from "@/components/SEO";
import { MaskedLines, Reveal, Marquee, SectionHead, Eyebrow } from "@/components/Motion";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0.15]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
  }, []);

  if (!settings) return <div className="min-h-screen bg-forest" />;
  const hero = settings.hero || {};
  const cats = settings.categories || {};
  const featured = products.filter((p) => p.featured).slice(0, 3);
  const marqueeItems = [
    "Cloves", "Black Pepper", "Green Tea", "Basmati Rice", "Cardamom", "CTC Tea", "Cinnamon",
    "Star Anise", "Black Tea", "Nutmeg", "Mace", "Bay Leaf", "Stone Flower", "Shahi Jeera", "Marathi Mogga",
  ];

  return (
    <div data-testid="home-page">
      <SEO title={settings.seo?.pages?.home?.title} description={settings.seo?.pages?.home?.description} path="/" />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden bg-forest-deep grain" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={hero.image} alt="Indian spices, rice and tea for export" className="h-[115%] w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest-deep/60 via-forest-deep/40 to-forest-deep" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative mx-auto flex min-h-screen max-w-[1440px] flex-col justify-center px-4 sm:px-8 lg:px-16 pt-24">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            data-testid="hero-eyebrow"
          >
            AnVibes™ · India → Global Markets
          </motion.p>
          <h1 className="mt-6 font-serif text-[2.6rem] sm:text-6xl lg:text-[5.2rem] leading-[1.04] tracking-tight text-parchment max-w-5xl">
            <MaskedLines
              lines={["Spices, Rice & Teas", "Bringing Indian Products", "to Global Markets"]}
              lineClassName="[&:nth-child(2)]:italic"
            />
          </h1>
          <motion.p
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#9EB8A8]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-subheadline"
          >
            {hero.subheadline}
          </motion.p>
          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/products"
              data-testid="hero-cta-explore"
              className="group inline-flex items-center gap-2.5 bg-gold px-7 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-forest transition-colors duration-300 hover:bg-gold-light"
            >
              Explore Products
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/quote"
              data-testid="hero-cta-quote-button"
              className="inline-flex items-center gap-2.5 border border-parchment/30 px-7 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-parchment transition-colors duration-300 hover:border-gold hover:text-gold-light"
            >
              Request a Quote
            </Link>
          </motion.div>

          <motion.div
            className="mt-16 grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-4 max-w-3xl"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            data-testid="hero-stats"
          >
            {(settings.stats || []).map((s) => (
              <div key={s.label} className="bg-forest-deep/70 backdrop-blur px-5 py-5">
                <p className="font-serif text-2xl sm:text-[1.75rem] leading-tight text-gold-light">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-parchment/60">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-parchment/50"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        >
          <ArrowDown className="h-5 w-5" />
        </motion.div>
      </section>

      <Marquee items={marqueeItems} />

      {/* CATEGORIES */}
      <section className="bg-parchment py-20 lg:py-28" data-testid="categories-section">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead eyebrow="Chapter 01 — What We Export" title="Spices, Rice & Teas." description="AnVibes Exports sources and supplies Indian spices, rice, and teas, including green tea, black tea, and CTC tea, for international buyers, wholesalers, distributors, and food businesses." />
            <Reveal delay={0.15}>
              <Link to="/products" data-testid="categories-view-all" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold-dark hover:text-forest transition-colors">
                View full catalog <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {["rice", "spices", "tea"].map((key, i) => {
              const c = cats[key] || {};
              return (
                <Reveal key={key} delay={i * 0.12}>
                  <div className="group relative" data-testid={`category-card-${key}`}>
                    <div className="relative aspect-[3/4] overflow-hidden clip-frame bg-forest">
                      {c.image && (
                        <img src={c.image} alt={`${c.title} — AnVibes Exports`} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/90 via-forest-deep/20 to-transparent" />
                      <span className="absolute right-4 top-4 font-serif italic text-6xl text-parchment/20">0{i + 1}</span>
                      <div className="absolute inset-x-0 bottom-0 p-7">
                        <h3 className="font-serif text-3xl text-parchment">{c.title}</h3>
                        <p className="mt-2 text-sm text-parchment/70 leading-relaxed">{c.description}</p>
                        <div className="mt-5 flex gap-2.5">
                          <Link to={`/${key}`} data-testid={`category-view-${key}`} className="inline-flex items-center gap-1.5 bg-parchment px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-gold">
                            View Products
                          </Link>
                          <Link to={`/quote?category=${key}`} data-testid={`category-quote-${key}`} className="inline-flex items-center border border-parchment/40 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-parchment transition-colors hover:border-gold hover:text-gold-light">
                            Request a Quote
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE — numbered manifesto chapters */}
      <section className="bg-forest py-20 lg:py-28 grain relative" data-testid="why-choose-section">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <SectionHead dark eyebrow="Chapter 02 — Why AnVibes Exports" title="Your Reliable Partner for B2B Trade" />
          <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {(settings.why_choose || []).map((item, i) => (
              <Reveal key={item.title} delay={(i % 3) * 0.1}>
                <div className="group h-full bg-forest p-8 lg:p-10 transition-colors duration-500 hover:bg-forest-mid" data-testid={`why-choose-item-${i + 1}`}>
                  <span className="font-serif italic text-4xl text-gold/50 transition-colors group-hover:text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-5 font-serif text-2xl text-parchment">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#9EB8A8]">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPORT PROCESS */}
      <section className="bg-parchment py-20 lg:py-28" data-testid="export-process-section">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <SectionHead eyebrow="Chapter 03 — Export Process" title="From inquiry to delivery, in nine clear steps." />
          <div className="mt-16 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {(settings.process || []).map((step, i) => (
              <Reveal key={step.step} delay={(i % 3) * 0.08}>
                <div className="relative border-t-2 border-forest/10 pt-6 group" data-testid={`process-step-${step.step}`}>
                  <span className="absolute -top-[11px] left-0 h-5 w-5 rounded-full border-2 border-gold bg-parchment transition-colors group-hover:bg-gold" />
                  <span className="font-mono text-xs tracking-[0.2em] text-gold-dark">STEP {String(step.step).padStart(2, "0")}</span>
                  <h3 className="mt-2.5 font-serif text-xl sm:text-2xl text-forest">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#4A554D]">{step.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16 text-center">
            <Link to="/quote" data-testid="process-cta" className="inline-flex items-center gap-2.5 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-parchment transition-colors hover:bg-forest-leaf">
              Start With Step One <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* GLOBAL MARKETS */}
      <section className="relative overflow-hidden bg-forest-deep py-20 lg:py-28 grain" data-testid="global-markets-section">
        <div className="absolute inset-0 dot-map opacity-40" />
        <div className="relative mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <SectionHead
              dark
              eyebrow="Chapter 04 — Global Reach"
              title={settings.markets?.headline || "From India to Global Markets"}
              description={settings.markets?.description}
            />
            <Reveal delay={0.15}>
              <div className="flex flex-wrap gap-3" data-testid="markets-regions">
                {(settings.markets?.regions || []).map((r) => (
                  <span key={r} className="inline-flex items-center gap-2 border border-gold/30 bg-forest/60 px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-parchment/85">
                    <Globe2 className="h-3.5 w-3.5 text-gold" /> {r}
                  </span>
                ))}
              </div>
              <Link to="/markets" data-testid="markets-explore-link" className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors">
                Explore export markets <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="bg-parchment py-20 lg:py-28" data-testid="featured-products-section">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
            <SectionHead eyebrow="Chapter 05 — Featured" title="Selected from the catalog." />
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* PRIVATE LABEL */}
      {settings.private_label?.enabled && (
        <section className="bg-forest py-20 lg:py-24 grain relative" data-testid="private-label-section">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 grid items-center gap-10 lg:grid-cols-2">
            <SectionHead dark eyebrow="Chapter 06 — Custom" title={settings.private_label.title} description={settings.private_label.description} />
            <Reveal delay={0.15}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(settings.private_label.options || []).map((o) => (
                  <li key={o} className="flex items-center gap-3 border border-white/10 bg-forest-mid/50 px-5 py-4 text-sm text-parchment/85">
                    <Package className="h-4 w-4 shrink-0 text-gold" /> {o}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="relative overflow-hidden bg-gold py-16 lg:py-20" data-testid="home-cta-band">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start gap-8 px-4 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:px-16">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-forest leading-tight max-w-2xl">
              Tell us your quantity, packaging and port. We'll handle the rest.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/quote" data-testid="cta-band-quote-button" className="inline-flex items-center gap-3 bg-forest px-9 py-5 text-sm font-semibold uppercase tracking-[0.16em] text-parchment transition-colors hover:bg-forest-leaf">
              Request a Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
